import Vue from 'vue'
import applogger from '@applogger'
import BlobLoadPostProcessor from './tools/blob-load-post-processor'
import ImageLoaderProcess from './tools/image-loader-process'

const state = () => ({
  data: {},
  blobsloading: {},
  blobProcessor: null
})

const actions = {
  registryBlobProcessorHandler({ state }, payload) {
    if (!state.blobProcessor) {
      state.blobProcessor = new BlobLoadPostProcessor()
    }
    state.blobProcessor.register(payload)
  },
  loadgroup({state, rootState, commit, getters, rootGetters}, payload) {
    const queryParams = payload.blobIDs.reduce((acc, id) => {
      let reqid = id.replace(/[^a-z0-9]/g, '')

      // Check if the promise to retreive the blobs is already pending.
      if (state.blobsloading[ reqid ]) return acc
      // Check if the blob is already loaded.
      else if (state.data[id]) return acc

      return {
        ids: { ...acc.ids, [ reqid ]: id },
        blobs: [ ...acc.blobs, `${reqid}: content(id: "${id}")` ]
      }
    }, {ids: {}, blobs: []})

    if (!Object.keys(queryParams.ids).length) return

    let query = queryParams.blobs.join('\n')
    query = getters['graphQLClient'].query({
      query: `query getBlobs { ${query} }`,
      opName: 'getBlobs'
    })

    // Generates the list of promises to return the queries promises for the blob content
    let promises = {}

    Object.values(queryParams.ids).forEach(blobid => {
      state.blobsloading[ blobid ] = new Promise((resolve, reject) => {
        promises[blobid] = { resolve, reject }
      })
    })

    let ImageLoader = new ImageLoaderProcess()
    return query
      .then(response => {
        if (response.errors) {
          throw new Error(response.errors[0].message)
        }
        else {
          return Object.entries(response.data).map(([blobID, blobData]) => {
            try {
              blobData = JSON.parse(blobData)
              promises[ queryParams.ids[blobID] ].resolve({ data: { content: blobData } })
              // Pass the blob to the Blob Processor to see if any components or registered function want
              // to perform any addition pre-processing while loading the blob. This is to potentially pre-fetch
              // the embedded image urls or pre-fetch the some remote content for the presentation of the blob
              return state.blobProcessor.process(blobID, blobData, {
                state: rootState,
                commit: this.commit,
                getters: rootGetters,
                dispatch: this.dispatch,
                filestore: payload.filestore || 'website',
                imageLoader: ImageLoader
              })
                .then(blobData => {
                  commit('STORE_BLOB', {id: queryParams.ids[blobID], data: blobData})
                })
            }
            catch (err) {
              commit('STORE_BLOB', {id: queryParams.ids[blobID], data: blobData})
            }
          })
        }
      })
      .then(loaders => Promise.all(loaders))
      .then(_ => ImageLoader.preLoadImageUrls(this.dispatch))
      .then(_ => {
        let bloblist = payload.blobIDs.join(', ')
        applogger.debug(`Blobs completed Loading: ${bloblist}`)
      })
      .catch(err => {
        let bloblist = payload.blobIDs.join(', ')
        applogger.debug(`Error Retreiving Content blobs: ${bloblist} - ${err.message}`)
        let statusMessage = {
          type: 'error',
          title: `Error loading content blobs: ${bloblist}`,
          text: `
            <p>There was an error encountered loading the Content on the Page.</p>
            <p>Please reload the page and if the problem persists please contact the
            site admin.</p>
          <p>Error: ${err.message}</p>
          `,
          notification: true,
          showClose: true
        }
        this.dispatch('reportError', statusMessage)
      })
      .finally(_ => {
        // removes the pending loaders for blobs that are loaded.
        Object.keys(queryParams.ids).forEach(blobID => delete state.blobsloading[ blobID ])
      })
  },
  load({state, rootState, commit, getters, rootGetters}, payload) {
    let blobID = payload.blobID
    const storeBlob = payload.storeBlob ?? true
    if (Object.prototype.hasOwnProperty.call(state.data, payload.blobID)) {
      return Promise.resolve(state.data[ payload.blobID ])
    }
    if (!Object.prototype.hasOwnProperty.call(state.blobsloading, payload.blobID)) {
      state.blobsloading[payload.blobID] = getters['graphQLClient'].query({
        query: `query getSingleBlob { content: content(id: "${blobID}") }`,
        opName: 'getSingleBlob'
      })
    }
    return state.blobsloading[payload.blobID]
      .then(response => {
        if (response.errors) {
          throw new Error(response.errors[0].message)
        }
        else {
          return JSON.parse(response.data.content)
        }
      })
      .then(blobData => {
        // Pass the blob to the Blob Processor to see if any components or registered function want
        // to perform any addition pre-processing while loading the blob. This is to potentially pre-fetch
        // the embedded image urls or pre-fetch the some remote content for the presentation of the blob
        if (!state.blobProcessor) {
          if (storeBlob) commit('STORE_BLOB', {id: blobID, data: blobData})
          return blobData
        }

        try {
          return state.blobProcessor.process(blobID, blobData, {state: rootState, commit: commit, getters: rootGetters, dispatch: this.dispatch, filestore: payload.filestore || 'website'})
            .then(blobData => {
              if (storeBlob) commit('STORE_BLOB', {id: blobID, data: blobData})
              return blobData
            })
        }
        catch (err) {
          commit('STORE_BLOB', {id: blobID, data: blobData})
          return blobData
        }
      })
      .catch(err => {
        applogger.debug('Error Retreiving blob: ' + payload.blobID + ' - ' + err.message)
        let statusMessage = {
          type: 'error',
          title: `Error loading content blob: ${payload.blobID}`,
          text: `
            <p>There was an error encountered loading the Content on the Page.</p>
            <p>Please reload the page and if the problem persists please contact the
            site admin.</p>
          <p>Error: ${err.message}</p>
          `,
          notification: true,
          showClose: true
        }
        this.dispatch('reportError', statusMessage)
      })
      .finally(_ => {
        delete state.blobsloading[payload.blobID]
      })
  },
  refresh({ state }, payload) {
    // let blobData = state.data[payload]
    // let options = merge({}, blobData.cacheOptions, {reset: true})
    return this.dispatch('blob/load', {blobID: payload})
  },
  save({commit, getters}, payload) {
    const mutation = `
      mutation saveBlob($id: ID!, $blobData: String!) {
        update(id: $id, content: $blobData)
      }
    `
    return getters['graphQLClient'].mutate({
      mutation,
      variables: {
        id: payload.blobID,
        blobData: JSON.stringify(payload.blobData)
      }
    })
      .then(response => response.data.update)
      .then(blob => JSON.parse(blob))
      .then(blob => {
        commit('STORE_BLOB', {id: payload.blobID, data: blob})
        return blob
      })
  }
}

const mutations = {
  STORE_BLOB(state, blob) {
    blob = {
      blobID: blob.id,
      data: blob.data,
      timestamp: blob.timestamp || new Date()
    }
    Vue.set(state.data, blob.blobID, blob)
  }
}

const getters = {
  graphQLClient: function(state, getters, rootState, rootGetters) {
    return rootGetters['getGraphQLClient']('thesaurum')
  },
  expired: function(state) {
    return function(blobID) {
      let blobData = state.data[blobID]
      return (blobData.timestamp + (blobData.cacheOptions.ttl * 1000)) >= new Date()
    }
  },
  get: function(state) {
    return (blobID) => (state.data[blobID]) ? state.data[blobID].data : {}
  },
  isHeader(state, getters) {
    return (blobID) => {
      const blob = getters.get(blobID)
      if (blob && blob.cardItems) {
        const cards = blob.cardItems.filter(item => item.cardType !== 'empty' && item.gridId == null)
        return (blob.widgetType === 'carousel' && cards.length > 0) || blob.widgetType === 'vimeo-video'
      }
      return false
    }
  }
}

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters
}
